import { memo, useEffect, useMemo, useRef } from 'react';
import { shallowEqual } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';
import { Grid, Spacer } from '@zillow/constellation';
import { useLocation } from 'react-router-dom';

import { Heading, Modal } from '@zillow/constellation-10';

import { useClientAppConfigWebApiHandler_getFeature } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../selectors/useSelector';

import { setQueryApplicationsSummary } from '../../hooks/queries/summary';
import accessUtils from '../../utils/accessUtils';
import SelectAvailableActions from '../../components/Modal/AddPropertyModal/components/SelectAvailableActions';

import ApplicationsModule from '../../components/ApplicationsModule';
import InterestedRenters from '../../components/InterestedRenters';
import InquiriesModule from '../../components/InquiriesModule';
import LeasesModule from '../../components/LeasesModule';
import ListingModule from '../../components/ListingModule';
import NextStepsModule from '../../components/NextStepsModule';
import PaymentsModule from '../../components/PaymentsModule';
import UpcomingToursModule from '../../components/UpcomingToursModule';
import ConnectedModuleCard from '../../components/ConnectedModuleCard';
import SimpleCard from '../../components/SimpleCard';
import TenantsModule from '../../components/TenantsModule';

interface LocationState {
  showSuccessModal?: boolean;
}

const OverviewPage = () => {
  const queryClient = useQueryClient();
  const { state: locationState = {} } = useLocation();

  const initialRender = useRef(true);

  const {
    active,
    isApplicationsAccepted,
    isApplicationsRestricted,
    isLeasesRestricted,
    isPaymentsRestricted,
    isEnterpriseCustomer,
    listingAlias,
  } = useSelector((state) => {
    return {
      active: state.properties.currentProperty.active,
      appConfig: state.app.appConfig,
      cookieEnabledServices: state.app.cookieEnabledServices,
      isApplicationsAccepted: state.properties.currentProperty.isApplicationsAccepted,
      isApplicationsRestricted:
        state?.properties?.currentProperty?.restrictions?.applications?.isAccessRestricted || false,
      isLeasesRestricted: state.properties.currentProperty.restrictions.isLeasesRestricted,
      isPaymentsRestricted: state.properties.currentProperty.restrictions.isPaymentsRestricted,
      isEnterpriseCustomer: state.user.isEnterpriseCustomer,
      listingAlias: state.properties.currentProperty.listingAlias,
      state: state.properties.currentProperty.state,
      zgApi: state.env.zgApi,
    };
  }, shallowEqual);

  const { isPending: isPendingCanAccessTenantModule, data: canAccessTenantModuleResponse = 'false' } =
    useClientAppConfigWebApiHandler_getFeature({
      appName: 'zrm_web',
      featureName: 'canAccessTenantModule',
    });

  const nextStepGridOffset = { default: -2, md: -3, xl: 0 };
  const { showSuccessModal } = locationState as LocationState;

  const canAccessTenantsModule = accessUtils.canAccessTenantsModule({
    canAccessTenantModuleResponse,
  });

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    syncWithQueryCache({ isApplicationsAccepted });
  }, [isApplicationsAccepted]);

  const syncWithQueryCache = ({ isApplicationsAccepted }: { isApplicationsAccepted: boolean }) => {
    setQueryApplicationsSummary({
      config: {
        queryClient,
        listingAlias,
        getActive: true,
      },
      payload: {
        isApplicationsAccepted,
      },
    });
  };

  const renderLoadingModule = (i: number) => (
    <Spacer data-testid="LoadingModuleContainer" marginBottom="sm">
      <ConnectedModuleCard key={i} title="loading" isLoading={true}>
        <SimpleCard isLoading={true} />
      </ConnectedModuleCard>
    </Spacer>
  );

  const renderActiveModules = useMemo(() => {
    if (!listingAlias || isPendingCanAccessTenantModule) {
      return [...Array(5)].map(renderLoadingModule);
    }

    return (
      <>
        <Spacer marginBottom="sm">
          <ListingModule />
        </Spacer>

        {canAccessTenantsModule && (
          <Spacer marginBottom="sm">
            <TenantsModule />
          </Spacer>
        )}

        {isEnterpriseCustomer ? (
          <Spacer data-testid="InquiriesContainer" marginBottom="sm">
            <InquiriesModule />
          </Spacer>
        ) : (
          <Spacer data-testid="InterestedRentersContainer" marginBottom="sm">
            <InterestedRenters />
          </Spacer>
        )}

        {isApplicationsRestricted !== true && (
          <Spacer data-testid="ApplicationsModuleContainer" marginBottom="sm">
            <ApplicationsModule />
          </Spacer>
        )}

        {isPaymentsRestricted !== true && (
          <Spacer data-testid="PaymentsModuleContainer" marginBottom="sm">
            <PaymentsModule />
          </Spacer>
        )}

        {isLeasesRestricted !== true && (
          <Spacer data-testid="LeasesModuleContainer" marginBottom="sm">
            <LeasesModule />
          </Spacer>
        )}
      </>
    );
  }, [
    canAccessTenantsModule,
    isApplicationsRestricted,
    isEnterpriseCustomer,
    isLeasesRestricted,
    isPaymentsRestricted,
    isPendingCanAccessTenantModule,
    listingAlias,
  ]);

  const renderInactiveModules = useMemo(() => {
    // if propertyDetailsOverview is loading, fill column with skeleton modules
    if (!listingAlias || isPendingCanAccessTenantModule) {
      return [...Array(5)].map(renderLoadingModule);
    }

    return (
      <>
        {isPaymentsRestricted !== true && (
          <Spacer data-testid="PaymentsModuleContainer" marginBottom="sm">
            <PaymentsModule />
          </Spacer>
        )}

        {canAccessTenantsModule && (
          <Spacer marginBottom="sm">
            <TenantsModule />
          </Spacer>
        )}

        {isLeasesRestricted !== true && (
          <Spacer data-testid="LeasesModuleContainer" marginBottom="sm">
            <LeasesModule />
          </Spacer>
        )}

        <Spacer marginBottom="sm">
          <ListingModule />
        </Spacer>
      </>
    );
  }, [canAccessTenantsModule, isLeasesRestricted, isPaymentsRestricted, isPendingCanAccessTenantModule, listingAlias]);

  return (
    <div data-testid="OverviewPage">
      {showSuccessModal && (
        <Modal
          open={showSuccessModal}
          scrollDocument={false}
          size={{ base: 'fullScreen', md: 'lg' }}
          header={<Heading level={2}>Zillow Rental Manager</Heading>}
          body={<SelectAvailableActions listingAlias={listingAlias} triggerObject="zrm_user_onboarding" />}
        />
      )}
      <Grid display="grid" gridTemplateColumns="repeat(12, minmax(0,1fr))" gap={{ xl_gte: 'sm' }}>
        <Grid data-testid="LeftColumn" gridColumn={{ default: '1/13', xl_gte: '1/8' }}>
          {active ? renderActiveModules : renderInactiveModules}
        </Grid>

        {/* override margin left and right to display as fluid */}
        <Grid
          data-testid="RightColumn"
          gridColumn={{ default: '1/13', xl_gte: '8/13' }}
          gridRowStart={{ default: '1', xl: 'inherit' }}
          marginLeft={nextStepGridOffset}
          marginRight={nextStepGridOffset}
          marginBottom={{ default: 'sm' }}
        >
          {/* if propertyDetailsOverview is loading, fill column with skeleton modules */}
          {listingAlias ? (
            <>
              <UpcomingToursModule />
              <NextStepsModule />
            </>
          ) : (
            [...Array(2)].map(renderLoadingModule)
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(OverviewPage);
